/**
 * @file Admin dashboard UI — pages/Dashboard/Admin/Materials/AdminStartReviewModal.tsx.
 */

import React, { useEffect, useState } from "react";
import axiosClient from "../../../../api/axiosClient";
import { API_URLS } from "../../../../constants/apiUrls";
import "./AdminStartReviewModal.css";

type FlagKind = "admin" | "contributor" | "expert";

type Expert = { id: string; name: string; email: string };

interface Props {
  open: boolean;
  onClose: () => void;
  submissionId: string;
  submissionCode: string;
  /** Required for “Flag + assign expert” (expert list). */
  materialId?: number | null;
  onReviewStarted?: () => void;
  onBack?: () => void;
}

const AdminStartReviewModal: React.FC<Props> = ({
  open,
  onClose,
  submissionId,
  submissionCode,
  materialId,
  onReviewStarted,
  onBack,
}) => {
  const [reason, setReason] = useState("");
  const [flagKind, setFlagKind] = useState<FlagKind>("admin");
  const [experts, setExperts] = useState<Expert[]>([]);
  const [expertId, setExpertId] = useState<string | null>(null);
  const [loadingExperts, setLoadingExperts] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setReason("");
      setFlagKind("admin");
      setExpertId(null);
      setError(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open || flagKind !== "expert" || materialId == null || Number.isNaN(Number(materialId))) {
      setExperts([]);
      setExpertId(null);
      return;
    }
    setLoadingExperts(true);
    axiosClient
      .get("/expert/admin/experts", { params: { materialId } })
      .then((res) => {
        const list: Expert[] = Array.isArray(res.data?.data) ? res.data.data : [];
        setExperts(list);
        if (list.length === 1) setExpertId(list[0].id);
        else setExpertId(null);
      })
      .catch(() => {
        setExperts([]);
        setError("Unable to load experts for this material.");
      })
      .finally(() => setLoadingExperts(false));
  }, [open, flagKind, materialId]);

  const handleSubmit = async () => {
    if (!reason.trim()) {
      setError("Please provide a reason for flagging this submission");
      return;
    }
    if (flagKind === "expert") {
      if (materialId == null) {
        setError("Material is required to assign an expert while flagging.");
        return;
      }
      if (!expertId) {
        setError("Please select an expert.");
        return;
      }
    }

    setSubmitting(true);
    setError(null);

    const url =
      flagKind === "admin"
        ? API_URLS.ADMIN.SUBMISSIONS.FLAG_ADMIN(submissionId)
        : flagKind === "contributor"
          ? API_URLS.ADMIN.SUBMISSIONS.FLAG_CONTRIBUTOR(submissionId)
          : API_URLS.ADMIN.SUBMISSIONS.FLAG_EXPERT(submissionId);

    const body =
      flagKind === "expert"
        ? { reason: reason.trim(), expertId }
        : { reason: reason.trim() };

    try {
      const res = await axiosClient.post(url, body);

      if (res.data?.success) {
        setReason("");
        onReviewStarted?.();
        onClose();
        if (onBack) onBack();
      } else {
        setError(res.data?.message || "Failed to flag submission");
      }
    } catch (err: unknown) {
      console.error("Flag submission error", err);
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message || "Failed to flag submission. Please try again.";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const expertDisabled =
    flagKind === "expert" &&
    (materialId == null || loadingExperts || experts.length === 0);

  return (
    <div className="start-review-overlay" onClick={onClose}>
      <div className="start-review-modal" onClick={(e) => e.stopPropagation()}>
        <header className="start-review-header">
          <div>
            <h2>Flag Dataset</h2>
            <p className="start-review-subtitle">
              Submission Code: <strong>{submissionCode}</strong>
            </p>
          </div>
          <button type="button" className="start-review-close" onClick={onClose}>
            ×
          </button>
        </header>

        <section className="start-review-body">
          <div className="start-review-field">
            <span className="start-review-label">Flag type</span>
            <div className="start-review-flag-options">
              <label>
                <input
                  type="radio"
                  name="flagKind"
                  checked={flagKind === "admin"}
                  onChange={() => setFlagKind("admin")}
                  disabled={submitting}
                />
                Flag only (not to contributor)
              </label>
              <label>
                <input
                  type="radio"
                  name="flagKind"
                  checked={flagKind === "contributor"}
                  onChange={() => setFlagKind("contributor")}
                  disabled={submitting}
                />
                Flag + return to contributor
              </label>
              <label>
                <input
                  type="radio"
                  name="flagKind"
                  checked={flagKind === "expert"}
                  onChange={() => setFlagKind("expert")}
                  disabled={submitting}
                />
                Flag + assign expert
              </label>
            </div>
          </div>

          {flagKind === "expert" && (
            <div className="start-review-field">
              <label htmlFor="flag-expert" className="start-review-label">
                Expert <span className="required-asterisk">*</span>
              </label>
              {materialId == null ? (
                <p className="start-review-hint">This submission has no material ID; use another flow or open the submission detail page.</p>
              ) : loadingExperts ? (
                <p className="start-review-hint">Loading experts…</p>
              ) : experts.length === 0 ? (
                <p className="start-review-hint">No experts available for this material.</p>
              ) : (
                <select
                  id="flag-expert"
                  className="start-review-select"
                  value={expertId ?? ""}
                  onChange={(e) => setExpertId(e.target.value || null)}
                  disabled={submitting}
                >
                  <option value="">Select expert…</option>
                  {experts.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.name} ({e.email})
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          <div className="start-review-field">
            <label htmlFor="review-reason" className="start-review-label">
              Reason <span className="required-asterisk">*</span>
            </label>
            <textarea
              id="review-reason"
              className="start-review-textarea"
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                setError(null);
              }}
              placeholder="Enter the reason for flagging this submission (e.g., Missing fuel density values in A2)"
              rows={6}
              disabled={submitting}
            />
          </div>

          {error && <div className="start-review-error">{error}</div>}
        </section>

        <footer className="start-review-actions">
          <button type="button" className="start-review-secondary" onClick={onClose} disabled={submitting}>
            Close
          </button>
          <button
            type="button"
            className="start-review-primary"
            onClick={handleSubmit}
            disabled={
              submitting ||
              !reason.trim() ||
              expertDisabled ||
              (flagKind === "expert" && (!expertId || materialId == null))
            }
          >
            {submitting ? "Submitting…" : "Submit"}
          </button>
        </footer>
      </div>
    </div>
  );
};

/** React component — Admin Start Review Modal. */
export default AdminStartReviewModal;
